import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobRole } from '~/shared/data-contract/job-role';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export type UseArchiveJobRolesParams = {
    readonly jobRoles: NonEmptyArray<JobRole | JobRoleDetails>;
};

export type UseArchiveJobRolesResult = UseMutationResult<unknown, Error, UseArchiveJobRolesParams>;

export const useArchiveJobRoles = (): UseArchiveJobRolesResult => {
    const { archiveJobRoles } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ jobRoles }: UseArchiveJobRolesParams) => {
            const { architectureId } = jobRoles[0];
            const [first, ...rest] = jobRoles;
            const jobRoleIds: NonEmptyArray<string> = [first.id, ...rest.map((role) => role.id)];

            const result = await archiveJobRoles({
                jobArchitectureId: architectureId,
                jobRoleIds,
            });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: (params) => {
            const { architectureId } = params.jobRoles[0];
            const clusterIds = new Set<string>();

            params.jobRoles.forEach((role) => {
                if (!is.nullOrUndefined(role.parentId)) {
                    clusterIds.add(role.parentId);
                }
            });

            clusterIds.forEach((clusterId) => {
                queryClient.cancelQueries({
                    queryKey: jobArchitectureKeys.rolesByCluster(architectureId, clusterId),
                });

                queryClient.setQueryData(
                    jobArchitectureKeys.rolesByCluster(architectureId, clusterId),
                    (old: readonly JobRole[] | undefined) => {
                        return old?.filter((jobRole) => !params.jobRoles.some((r) => r.id === jobRole.id));
                    },
                );
            });
        },
        onSuccess: (_, { jobRoles }) => {
            const { architectureId } = jobRoles[0];

            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architecture(architectureId),
            });
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architectureDetail(architectureId),
            });
        },
        throwOnError: false,
    });
};
